import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronsLeft,
	ChevronsRight,
	ChevronUp,
	ExternalLink,
	Eye,
	Package,
	Printer,
	RefreshCw,
	Search
} from 'lucide-react';
import type { OrderToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Pill } from '~/components/pill';
import { toGreekCaps } from '~/lib/i18n/toGreekCaps';
import { formatCurrency } from '~/lib/dashboard/format';
import { formatDateTime } from '~/lib/format-date';
import { useSkroutzOrdersList, useOrderStatuses, openSkroutzExternalLink, type SkroutzExternalLink } from '~/hooks/use-skroutz-orders';

const PAGE_SIZES = [10, 20, 50, 100];

/** Skroutz orders (Λίστα Παραγγελιών Skroutz) — read-only, server-paged grid mirroring the legacy admin. */
export function SkroutzOrdersGridView() {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const [search, setSearch] = useState('');
	const [searchInput, setSearchInput] = useState('');
	const [searchId, setSearchId] = useState('');
	const [searchIdInput, setSearchIdInput] = useState('');
	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
	const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

	// Debounce the free-text inputs into the query params (mirrors the products/orders grids).
	useEffect(() => {
		const h = setTimeout(() => {
			setSearch(searchInput);
			setSearchId(searchIdInput);
			setPageIndex(0);
		}, 350);
		return () => clearTimeout(h);
	}, [searchInput, searchIdInput]);

	const { data, isLoading, isError, isFetching, refetch } = useSkroutzOrdersList({
		skip: pageIndex * pageSize,
		take: pageSize,
		search,
		searchID: searchId,
		sortDir
	});
	const { data: statuses } = useOrderStatuses();

	const statusName = useMemo(() => {
		const map = new Map<number, string>();
		for (const s of statuses ?? []) if (s.id != null) map.set(s.id, s.nm ?? '');
		return (id?: number) => (id != null ? (map.get(id) ?? String(id)) : '—');
	}, [statuses]);

	const rows = data?.items ?? [];
	const total = data?.totalCount ?? 0;
	const pageCount = Math.max(1, Math.ceil(total / pageSize));
	const from = total === 0 ? 0 : pageIndex * pageSize + 1;
	const to = Math.min((pageIndex + 1) * pageSize, total);

	const openLink = async (uid: string | null | undefined, kind: SkroutzExternalLink) => {
		if (!uid) return;
		try {
			const ok = await openSkroutzExternalLink(uid, kind);
			if (!ok) toast.error(t('orders.skroutz.noLink'));
		} catch {
			toast.error(t('orders.skroutz.linkError'));
		}
	};

	if (isError) {
		return (
			<div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface py-16 text-center">
				<Package size={40} className="text-danger-fg" strokeWidth={1.25} />
				<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
				<Button variant="outline" onClick={() => void refetch()}>
					{t('common.retry')}
				</Button>
			</div>
		);
	}

	return (
		<div className="flex flex-col gap-3">
			{/* Page head */}
			<div className="flex items-start justify-between gap-4 border-b border-border pb-4">
				<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{t('orders.skroutz.title')}</h1>
			</div>

			{/* Toolbar: search + id filter */}
			<div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface p-3">
				<div className="relative min-w-60 flex-1 md:max-w-md">
					<Search size={16} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-foreground-subtle" />
					<Input
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
						placeholder={t('orders.skroutz.searchPlaceholder')}
						className="pl-8"
						aria-label={t('common.search')}
					/>
				</div>
				<Input
					value={searchIdInput}
					onChange={e => setSearchIdInput(e.target.value)}
					placeholder={t('orders.skroutz.searchIdPlaceholder')}
					className="md:max-w-48"
					aria-label={t('orders.skroutz.searchId')}
				/>
			</div>

			{/* Grid bar: range + refresh */}
			<div className="flex items-center justify-between gap-4 rounded-t-md border border-b-0 border-border bg-elevated px-3 py-2 text-xs text-foreground-muted">
				<span>
					{t('system.grid.showing')}{' '}
					<strong className="font-medium tabular-nums text-foreground">
						{from}–{to}
					</strong>{' '}
					{t('system.grid.of')} <strong className="font-medium tabular-nums text-foreground">{total.toLocaleString('el-GR')}</strong>
				</span>
				<button
					type="button"
					onClick={() => void refetch()}
					title={t('common.refresh')}
					className="rounded-sm p-1.5 text-foreground-muted hover:bg-muted hover:text-foreground">
					<RefreshCw size={13} className={isFetching ? 'animate-spin' : undefined} />
				</button>
			</div>

			{/* Table */}
			<div className="overflow-x-auto rounded-b-md border border-border bg-surface">
				<table className="w-full border-collapse text-left text-sm" style={{ minWidth: 1000 }}>
					<thead>
						<tr className="border-b border-border bg-elevated text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
							<th className="px-3 py-2">{toGreekCaps(t('orders.skroutz.columns.code'))}</th>
							<th className="px-3 py-2">
								<button
									type="button"
									onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
									className="inline-flex items-center gap-1 hover:text-foreground">
									{toGreekCaps(t('orders.skroutz.columns.purchaseDate'))}
									{sortDir === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
								</button>
							</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.skroutz.columns.firstName'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.skroutz.columns.lastName'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.skroutz.columns.email'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.skroutz.columns.phone'))}</th>
							<th className="px-3 py-2">{toGreekCaps(t('orders.skroutz.columns.status'))}</th>
							<th className="px-3 py-2 text-right">{toGreekCaps(t('orders.skroutz.columns.total'))}</th>
							<th className="px-3 py-2 text-right">{toGreekCaps(t('orders.skroutz.columns.quantity'))}</th>
							<th className="px-3 py-2" />
						</tr>
					</thead>
					<tbody>
						{isLoading ? (
							<tr>
								<td colSpan={10} className="p-4">
									<Skeleton className="h-72 w-full rounded-md" />
								</td>
							</tr>
						) : rows.length === 0 ? (
							<tr>
								<td colSpan={10}>
									<div className="flex flex-col items-center gap-2 py-16 text-center">
										<Package size={36} className="text-foreground-disabled" strokeWidth={1.25} />
										<p className="text-sm text-foreground-muted">{t('common.noResults')}</p>
									</div>
								</td>
							</tr>
						) : (
							rows.map((r: OrderToken) => (
								<tr key={r.id ?? r.uid} className="border-b border-border-subtle last:border-b-0 hover:bg-elevated">
									<td className="px-3 py-2 text-center font-mono text-xs text-foreground-muted">{r.uid || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{formatDateTime(r.pdt)}</td>
									<td className="px-3 py-2 text-foreground">{r.fnm || '—'}</td>
									<td className="px-3 py-2 text-foreground">{r.lnm || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{r.eml || '—'}</td>
									<td className="px-3 py-2 text-foreground-muted">{r.mob || '—'}</td>
									<td className="px-3 py-2">
										<Pill variant="neutral">{statusName(r.st)}</Pill>
									</td>
									<td className="px-3 py-2 text-right tabular-nums text-foreground">{formatCurrency(r.gamt ?? 0)}</td>
									<td className="px-3 py-2 text-right tabular-nums text-foreground-muted">{r.total_quantity ?? 0}</td>
									<td className="px-3 py-2">
										<span className="flex items-center justify-end gap-0.5">
											<Button
												variant="ghost"
												size="icon"
												aria-label={t('orders.skroutz.actions.details')}
												title={t('orders.skroutz.actions.details')}
												onClick={() => r.id && navigate(`/orders/${r.id}`)}>
												<Eye size={15} />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												aria-label={t('orders.skroutz.actions.showOnSkroutz')}
												title={t('orders.skroutz.actions.showOnSkroutz')}
												onClick={() => void openLink(r.uid, 'show_on_skroutz')}>
												<ExternalLink size={15} />
											</Button>
											<Button
												variant="ghost"
												size="icon"
												aria-label={t('orders.skroutz.actions.printVoucher')}
												title={t('orders.skroutz.actions.printVoucher')}
												onClick={() => void openLink(r.uid, 'courier_voucher')}>
												<Printer size={15} className="text-amber-600" />
											</Button>
										</span>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{/* Pager */}
			<div className="mt-1 flex flex-wrap items-center justify-between gap-3 rounded-md border border-border bg-surface px-3 py-2 text-xs text-foreground-muted">
				<div className="flex items-center gap-2">
					<span>{t('system.grid.rowsPerPage')}</span>
					<Select
						value={String(pageSize)}
						onValueChange={v => {
							setPageSize(Number(v));
							setPageIndex(0);
						}}>
						<SelectTrigger className="h-8 w-20">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{PAGE_SIZES.map(size => (
								<SelectItem key={size} value={String(size)}>
									{size}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex items-center gap-1">
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex === 0}
						onClick={() => setPageIndex(0)}
						aria-label={t('system.grid.first')}>
						<ChevronsLeft size={15} />
					</Button>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex === 0}
						onClick={() => setPageIndex(p => Math.max(0, p - 1))}
						aria-label={t('system.grid.prev')}>
						<ChevronLeft size={15} />
					</Button>
					<span className="px-2 tabular-nums">
						{pageIndex + 1} / {pageCount}
					</span>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex >= pageCount - 1}
						onClick={() => setPageIndex(p => Math.min(pageCount - 1, p + 1))}
						aria-label={t('system.grid.next')}>
						<ChevronRight size={15} />
					</Button>
					<Button
						variant="outline"
						size="icon"
						type="button"
						disabled={pageIndex >= pageCount - 1}
						onClick={() => setPageIndex(pageCount - 1)}
						aria-label={t('system.grid.last')}>
						<ChevronsRight size={15} />
					</Button>
				</div>
			</div>
		</div>
	);
}
